import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Application from "../../../StateContext/Application.js";
import { apiurl } from "../../../../../path.js";
import { useLanguage } from "../../../../../lang/langcontext/LanguageContext.js";
import "./DemandGenral.css";

const EditDemandGeneration = () => {
  const { demandId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const operation = useContext(Application);
  const {
    GetBillByDemandID,
    EditDemandGeneration,
    DeductionOperation,
    GetWorkDetailbyworkID,
  } = operation;

  const submitLockRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bill, setBill] = useState(null);

  // ── Editable amount fields + Remark ──
  const [data, setData] = useState({
    BillAmount: "",
    GST: "",
    Royalty: "",
    SQMCharges: "",     // ← new
    TotalBillAmount: "",
    Remark: "",
  });

  const [fetchDeductionData, setFetchDeductionData] = useState([]);
  const [totalDeductionAmount, setTotalDeductionAmount] = useState(0);
  const [GrossAmount, setGrossAmount] = useState(0);
  const [netPayableAmount, setNetPayableAmount] = useState(0);

  // ── Previous RA Bill history for this work order (read-only table) ──
  const [workOrderData, setWorkOrderData] = useState([]);

  // ── same button styles as DemandGeneration.js, reused as-is ──
  const buttonStylePrimary = {
    cursor: "pointer",
    background: "linear-gradient(to right, #3b409e, #0067ab, #0f79ba, #0098d5)",
    color: "white",
    border: "none",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    whiteSpace: "nowrap",
  };

  const buttonStyleDanger = {
    cursor: "pointer",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    whiteSpace: "nowrap",
  };

  // ── same read-only field style used across the create page ──
  const readOnlyStyle = {
    backgroundColor: "#f0f4ff",
    color: "#3b409e",
    fontWeight: "600",
    cursor: "not-allowed",
    border: "1px solid #b0bfff",
    borderRadius: "6px",
    padding: "8px 12px",
    width: "100%",
  };

  // ── same helper as DemandGeneration.js: Bill + GST + Royalty ──
  const calculateTotal = (bill, gst, royalty, sqmCharges) => {
    return (
      Number(bill || 0) +
      Number(gst || 0) +
      Number(royalty || 0) +
      Number(sqmCharges || 0)
    ).toFixed(2);
  };

  // ── used ONLY for the initial display on load: Bill - GST - Royalty ──
  const calculateInitialTotal = (bill, gst, royalty, sqmCharges) => {
    return Number(
      (Number(bill || 0) - Number(gst || 0) - Number(royalty || 0) - Number(sqmCharges || 0)).toFixed(2),
    );
  };

  // ── load the bill + its deductions + prior RA bill history ──
  const loadBill = async () => {
    setLoading(true);
    try {
      const res = await GetBillByDemandID(demandId);
      setBill(res);

      setData({
        BillAmount: calculateInitialTotal(res.BillAmount, res.GST, res.Royalty, res.SQMCharges),
        GST: res.GST || "",
        Royalty: res.Royalty || "",
        SQMCharges: res.SQMCharges || "",
        TotalBillAmount: calculateTotal(
          calculateInitialTotal(res.BillAmount, res.GST, res.Royalty, res.SQMCharges),
          res.GST,
          res.Royalty,
          res.SQMCharges,
        ),
        Remark: res.Remark || "",
      });

      // merge master deduction/bank list with whatever was actually saved
      const allDeductions = await DeductionOperation(
        "null",
        "fetchalldatafromBank",
      );
      const existing = res.Deductions || [];
      // ✅ BUG FIX: match ONLY by deduction name (TypeOfAmount/UseFor).
      // Matching by Account_Number as a fallback was wrong — several
      // deduction types in the `banks` master list share the same
      // (dummy/test) account number, so every one of them was inheriting
      // whichever saved row happened to share that account number,
      // even though it was never actually applied to this bill.
      const merged = (allDeductions || []).map((d) => {
        const match = existing.find((e) => e.TypeOfAmount === d.UseFor);
        return { ...d, DeductionAmount: match ? Number(match.TotalAmount) : 0 };
      });
      setFetchDeductionData(merged);

      // ✅ IMPORTANT: seed the running total from the deductions we just
      // loaded — otherwise "Total Deduction Amount" / "Net Payable Amount"
      // show 0 / full-gross until the user manually edits a row.
      const initialDeductionTotal = merged.reduce(
        (sum, item) => sum + Number(item.DeductionAmount || 0),
        0,
      );
      setTotalDeductionAmount(initialDeductionTotal);

      // read-only "previous RA bills" history table, same shape as the
      // create page's RA-bill-number lookup table
      const workOrderId = res.WorkOrderID || res.WO_WorkOrderID;
      if (workOrderId && GetWorkDetailbyworkID) {
        try {
          const historyRes = await GetWorkDetailbyworkID(workOrderId);
          setWorkOrderData(historyRes?.message || []);
        } catch (err) {
          console.log("Work order history fetch failed:", err);
          setWorkOrderData([]);
        }
      }
    } catch (err) {
      console.log(err);
      alert("Failed to load bill details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBill();
    // eslint-disable-next-line
  }, [demandId]);

  // ✅ prevent scroll wheel from changing number input values (ported from DemandGeneration.js)
  useEffect(() => {
    const preventWheel = () => {
      if (document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    };
    window.addEventListener("wheel", preventWheel, { passive: true });
    return () => window.removeEventListener("wheel", preventWheel);
  }, []);

  // ── recompute totals whenever amounts or deductions change (same as create page) ──
  useEffect(() => {
    const gross = parseFloat(data.TotalBillAmount || 0);
    const totalDeduction = parseFloat(totalDeductionAmount || 0);
    setGrossAmount(parseFloat(gross.toFixed(2)));
    setNetPayableAmount(parseFloat((gross - totalDeduction).toFixed(2)));
  }, [data.TotalBillAmount, totalDeductionAmount]);

  const getRemainingCap = () =>
    Number(
      bill?.WO_BillRemainingAmount ?? bill?.WO_RemainingAmount ?? Infinity,
    );

  const handleBillAmountChange = (e) => {
    let value = e.target.value;
    if (Number(value) < 0) value = 0;

    const totalAmt = calculateTotal(value, data.GST, data.Royalty, data.SQMCharges);
    const remaining = getRemainingCap();

    if (remaining !== Infinity && totalAmt > remaining) {
      alert(
        `Total Bill Amount (₹${totalAmt}) cannot exceed the Remaining Amount available on this work order (₹${remaining}).`,
      );
      return; // reject the change, same pattern as create page
    }

    setData((prev) => ({
      ...prev,
      BillAmount: value,
      TotalBillAmount: totalAmt,
    }));
  };

  const handleGstChange = (e) => {
    let value = e.target.value;
    if (Number(value) < 0) value = 0;

    const totalAmt = calculateTotal(data.BillAmount, value, data.Royalty, data.SQMCharges);
    const remaining = getRemainingCap();

    if (remaining !== Infinity && totalAmt > remaining) {
      alert(
        `Total Bill Amount (₹${totalAmt}) cannot exceed the Remaining Amount available on this work order (₹${remaining}).`,
      );
      return;
    }

    setData((prev) => ({
      ...prev,
      GST: value,
      TotalBillAmount: totalAmt,
    }));
  };

  const handleRoyaltyChange = (e) => {
    let value = e.target.value;
    if (Number(value) < 0) value = 0;

    const totalAmt = calculateTotal(data.BillAmount, data.GST, value, data.SQMCharges);
    const remaining = getRemainingCap();

    if (remaining !== Infinity && totalAmt > remaining) {
      alert(
        `Total Bill Amount (₹${totalAmt}) cannot exceed the Remaining Amount available on this work order (₹${remaining}).`,
      );
      return;
    }

    setData((prev) => ({
      ...prev,
      Royalty: value,
      TotalBillAmount: totalAmt,
    }));
  };

  // ── SQM Charges (EDITABLE) — new, same pattern as Royalty ──
const handleSQMChargesChange = (e) => {
  let value = e.target.value;
  if (Number(value) < 0) value = 0;

  const totalAmt = calculateTotal(data.BillAmount, data.GST, data.Royalty, value);
  const remaining = getRemainingCap();

  if (remaining !== Infinity && totalAmt > remaining) {
    alert(
      `Total Bill Amount (₹${totalAmt}) cannot exceed the Remaining Amount available on this work order (₹${remaining}).`,
    );
    return;
  }

  setData((prev) => ({
    ...prev,
    SQMCharges: value,
    TotalBillAmount: totalAmt,
  }));
};

  // ── Remark change (editable) ──
  const handleRemarkChange = (e) => {
    setData((prev) => ({ ...prev, Remark: e.target.value }));
  };

  // ── same validation pattern as DemandGeneration.js's handleDeductionAmountChange ──
  const handleDeductionAmountChange = (e, index) => {
    const rawValue = parseFloat(e.target.value || 0);

    if (rawValue === "") {
      const updatedList = [...fetchDeductionData];
      updatedList[index].DeductionAmount = "";
      setFetchDeductionData(updatedList);

      const totalDeduction = updatedList.reduce(
        (sum, item) => sum + parseFloat(item.DeductionAmount || 0),
        0,
      );
      setTotalDeductionAmount(totalDeduction);
      return;
    }
    let value = e.target.value === "" ? "" : parseFloat(e.target.value);

    if (value === "") {
      const updatedList = [...fetchDeductionData];
      updatedList[index].DeductionAmount = "";
      setFetchDeductionData(updatedList);
      return;
    }
    
    const totalBillAmount = parseFloat(data.TotalBillAmount || 0);

    if (value < 0) {
      alert("Deduction Amount should be greater than 0 !");
      return;
    }

    const updatedList = [...fetchDeductionData];
    updatedList[index].DeductionAmount = value;

    const totalDeduction = updatedList.reduce(
      (sum, item) => sum + parseFloat(item.DeductionAmount || 0),
      0,
    );

    if (totalDeduction > totalBillAmount) {
      alert(
        `Total deduction (₹${totalDeduction}) cannot exceed Total Bill Amount (₹${totalBillAmount})`,
      );
      updatedList[index].DeductionAmount = 0;
      const correctedTotal = updatedList.reduce(
        (sum, item) => sum + parseFloat(item.DeductionAmount || 0),
        0,
      );
      setFetchDeductionData(updatedList);
      setTotalDeductionAmount(correctedTotal);
      return;
    }

    setFetchDeductionData(updatedList);
    setTotalDeductionAmount(totalDeduction);
  };

  // ✅ NEW — revert empty deduction field to 0 on blur
  const handleDeductionAmountBlur = (e, index) => {
    if (e.target.value === "") {
      const updatedList = [...fetchDeductionData];
      updatedList[index].DeductionAmount = 0;
      setFetchDeductionData(updatedList);

      const totalDeduction = updatedList.reduce(
        (sum, item) => sum + parseFloat(item.DeductionAmount || 0),
        0,
      );
      setTotalDeductionAmount(totalDeduction);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    if (!data.BillAmount || Number(data.BillAmount) <= 0) {
      alert("Bill Amount must be greater than 0!");
      submitLockRef.current = false;
      return;
    }

    // ✅ BUG FIX: re-check the remaining-amount cap right before submit too,
    // not just on keystroke, so a stale/loaded value can't slip through.
    const remainingCap = getRemainingCap();
    const totalBillAmountCheck = calculateTotal(
      data.BillAmount,
      data.GST,
      data.Royalty,
      data.SQMCharges,   // ← new
    );
    if (remainingCap !== Infinity && totalBillAmountCheck > remainingCap) {
      alert(
        `Total Bill Amount (₹${totalBillAmountCheck}) cannot exceed the Remaining Amount available on this work order (₹${remainingCap}).`,
      );
      submitLockRef.current = false;
      return;
    }

    setSaving(true);
    try {
      const finalDeductionList = fetchDeductionData.filter(
        (d) => d.DeductionAmount && d.DeductionAmount > 0,
      );

      const totalBillAmount = calculateTotal(
        data.BillAmount,
        data.GST,
        data.Royalty,
        data.SQMCharges,   // ← new
      );
      const computedDeductionTotal = finalDeductionList.reduce(
        (sum, item) => sum + Number(item.DeductionAmount || 0),
        0,
      );
      const computedNetPayable = totalBillAmount - computedDeductionTotal;

      const payload = {
        DemandGenerationID: demandId,
        BillAmount: data.BillAmount,
        GST: data.GST,
        Royalty: data.Royalty,
        SQMCharges: data.SQMCharges,   // ← new
        TotalBillAmount: totalBillAmount,
        totalTax: computedDeductionTotal,
        NetPayableAmount: computedNetPayable,
        Remark: data.Remark || "",
        DeductionList: JSON.stringify(finalDeductionList),
      };

      const result = await EditDemandGeneration(payload);

      if (result?.message) {
        alert(result.message);
        navigate("/AdminDashboard/ViewDemandGeneration");
      } else if (result?.error) {
        alert(result.error);
      }
    } catch (err) {
      console.log(err);
      alert("Something went wrong while saving.");
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(4px)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            border: "6px solid #f3f3f3",
            borderTop: "6px solid #007bff",
            borderRadius: "50%",
            width: "60px",
            height: "60px",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`@keyframes spin {0% { transform: rotate(0deg); }100% { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="col-12 text-center p-5 text-danger fw-bold">
        {t("Bill not found")}
      </div>
    );
  }

  // best-effort field reads off the raw demand_generation row — see note at top of file
  const workOrderTypeDisplay = bill.WorkOrderType || bill.workOrderType || "—";
  const headCodeDisplay =
    bill.BudgetHead || bill.HeadName || bill.BudgetHeadID || "—";
  const approvedYearDisplay = bill.ProposalYear || "—";
  const zillaDisplay = bill.District || "—";
  const talukaDisplay = bill.ULB || bill.Taluka || bill.ulb || "—";
  const mhuidDisplay = bill.MHUID_Number || bill.TechnicalSanctionUID || "—";
  const workNameDisplay = bill.MainWorkOrderID || bill.WorkName || "—";
  const contractorDisplay = bill.ContractorName || "—";
  const physicalProgressDisplay = bill.physicalValueOfProgress
    ? `${bill.physicalValueOfProgress}%`
    : "—";
  const raBillDisplay = bill.RABillNumber || "—";

  const billCopyFile = bill.BillCopy || "";
  const workOrderCopyFile = bill.CopyofWorkOrder || "";
  const measurementBookFile = bill.Measurement_Book || "";
  const inspectionReportFile = bill.Inspection_Report || "";
  const workCompletionFile = bill.Work_completion || "";
  const grCopyFile = bill.GRCopy || "";
  const fileBaseUrl = `${apiurl}`;

  return (
    <div
      className="col-12"
      style={{
        backgroundColor: "#EFF3F8",
        overflowY: "scroll",
        height: "90vh",
      }}
    >
      <div className="col-12 m-auto my-5 budgetContainer">
        <div className="m-auto DemandElement">
          {saving && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(4px)",
                zIndex: 9999,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  border: "6px solid #f3f3f3",
                  borderTop: "6px solid #007bff",
                  borderRadius: "50%",
                  width: "60px",
                  height: "60px",
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>
          )}

          <div className="row px-1 mb-4 mt-3">
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src="/Bill-files64.png"
                alt="EditBill"
                style={{ width: "32px", height: "32px" }}
                className="my-auto me-2"
              />
              <h3 className="text-left fw-bold mt-2">
                {t("Edit Bill")} — {bill.DemandGenerationID}
              </h3>
            </div>
            <div className="col-12 mt-4">
              <p
                className="py-2 ps-1 fw-bold text-danger"
                style={{ backgroundColor: "#FBE7E7" }}
              >
                {t(
                  "Note : Only Bill Amount, GST, Royalty and the Deduction Amounts below can be edited. All other details are read-only.",
                )}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* ── Work Order Type (read-only) ── */}
            <div className="col-12 elements mb-1 p-0">
              <div className="row g-0 p-0 m-0">
                <div className="col-3 my-auto">
                  <label>{t("Work Order Type")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={workOrderTypeDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Head Code (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3 my-auto">
                  <label>{t("Head Code")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={headCodeDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Approved Year (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Approved Year")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={approvedYearDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Zilla Parishad (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Zilla Parishad")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={zillaDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Taluka (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Taluka")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={talukaDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── MHUID Number (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("MHUID Number")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={mhuidDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Work Name (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Work Name")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={workNameDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Contractor Name (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Contractor Name")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={contractorDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Physical Progress (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Physical Progress In %")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={physicalProgressDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── Vendor Detail Table (read-only, same table as create page) ── */}
            <div className="col-12 my-3">
              <div className="row">
                <div className="col-12">
                  <div className="tableContainer m-auto">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: "15%", textAlign: "start" }}>
                            {t("Vendor Name")}
                          </th>
                          <th style={{ width: "20%", textAlign: "start" }}>
                            {t("Work Approve Amount")}
                          </th>
                          <th style={{ width: "15%", textAlign: "start" }}>
                            {t("Remaining Amount")}
                          </th>
                          <th style={{ width: "15%", textAlign: "start" }}>
                            {t("Bank Name")}
                          </th>
                          <th style={{ width: "15%", textAlign: "start" }}>
                            {t("Bank Account Number")}
                          </th>
                          <th style={{ width: "10%", textAlign: "start" }}>
                            {t("Bank IFSC")}
                          </th>
                          <th style={{ width: "15%", textAlign: "start" }}>
                            {t("Bank Branch Name")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "start" }}>
                            {contractorDisplay}
                          </td>
                          <td style={{ textAlign: "start" }}>
                            {bill.WO_ApprovedWorkAmount || "—"}
                          </td>
                          <td style={{ textAlign: "start" }}>
                            {bill.WO_BillRemainingAmount
                              ? parseFloat(bill.WO_BillRemainingAmount).toFixed(
                                  2,
                                )
                              : "—"}
                          </td>
                          <td style={{ textAlign: "start" }}>
                            {bill.Vendor_BANK_NAME || "—"}
                          </td>
                          <td style={{ textAlign: "start" }}>
                            {bill.Vendor_Account_Number || "—"}
                          </td>
                          <td style={{ textAlign: "start" }}>
                            {bill.Vendor_IFSC_CODE || "—"}
                          </td>
                          <td style={{ textAlign: "start" }}>
                            {bill.Vendor_BranchName || "—"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Bill Amount (EDITABLE) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>
                    {t("Bill Amount")} <span style={{ color: "red" }}>*</span>
                  </label>
                </div>
                <div className="col-7">
                  <input
                    type="number"
                    step="0.01"
                    name="BillAmount"
                    className="py-2"
                    placeholder={t("Enter Amount")}
                    value={data.BillAmount}
                    min={0}
                    onChange={handleBillAmountChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* ── GST (EDITABLE) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("GST")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="number"
                    name="GST"
                    className="py-2"
                    placeholder={t("Enter GST")}
                    value={data.GST}
                    min={0}
                    onChange={handleGstChange}
                  />
                </div>
              </div>
            </div>

            {/* ── Royalty (EDITABLE) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Royalty")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="number"
                    name="Royalty"
                    className="py-2"
                    placeholder={t("Enter Royalty")}
                    value={data.Royalty}
                    min={0}
                    onChange={handleRoyaltyChange}
                  />
                </div>
              </div>
            </div>

            {/* ── SQM Charges (EDITABLE) ── */}
<div className="col-12 elements">
  <div className="row">
    <div className="col-3">
      <label>{t("SQM Charges")}</label>
    </div>
    <div className="col-7">
      <input
        type="number"
        step="0.01"
        name="SQMCharges"
        className="py-2"
        placeholder={t("Enter SQM Charges")}
        value={data.SQMCharges}
        min={0}
        onChange={handleSQMChargesChange}
      />
    </div>
  </div>
</div>

            {/* ── Total Bill Amount (auto, read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Total Bill Amount")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={data.TotalBillAmount}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>
            </div>

            {/* ── RA Bill Number (read-only) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("RA Bill Number")}</label>
                </div>
                <div className="col-7">
                  <input
                    type="text"
                    value={raBillDisplay}
                    readOnly
                    style={readOnlyStyle}
                  />
                </div>
              </div>

              {/* ── Previous RA Bills history table (read-only, same table as create page) ── */}
              {/* {workOrderData && workOrderData.length > 0 && (
                <div className="tableContainer m-auto border-none">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ padding: "10px" }}>Sr No</th>
                        <th style={{ padding: "10px" }}>RA Bill</th>
                        <th style={{ padding: "10px" }}>Project Name</th>
                        <th style={{ padding: "10px" }}>Bill Amount</th>
                        <th style={{ padding: "10px" }}>Net Payable</th>
                        <th style={{ padding: "10px" }}>Deduction</th>
                        <th style={{ padding: "10px" }}>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workOrderData.map((item, index) => (
                        <tr key={index}>
                          <td style={{ padding: "10px" }}>{index + 1}</td>
                          <td style={{ padding: "10px" }}>
                            {item.RABillNumber}
                          </td>
                          <td style={{ padding: "10px" }}>
                            {item.ProjectName}
                          </td>
                          <td style={{ padding: "10px" }}>
                            ₹ {item.BillAmount}
                          </td>
                          <td style={{ padding: "10px" }}>
                            ₹ {item.NetPayableAmount}
                          </td>
                          <td style={{ padding: "10px" }}>
                            ₹ {item.DeductionAmount}
                          </td>
                          <td style={{ padding: "10px" }}>
                            {item.physicalValueOfProgress}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )} */}
            </div>

            {/* ── Remark (EDITABLE) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Remark")}</label>
                </div>
                <div className="col-7">
                  <textarea
                    type="text"
                    name="Remark"
                    className="py-2"
                    placeholder={t("Enter Remark")}
                    value={data.Remark}
                    onChange={handleRemarkChange}
                  />
                </div>
              </div>
            </div>

            {/* ── Uploaded Bill Copy (read-only link, no re-upload) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Bill Copy")}</label>
                </div>
                <div className="col-7">
                  {billCopyFile ? (
                    <a
                      href={`${fileBaseUrl}/BillCopyDocsImages/${billCopyFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...readOnlyStyle,
                        display: "inline-block",
                        textDecoration: "none",
                      }}
                    >
                      📄 {billCopyFile}
                    </a>
                  ) : (
                    <input
                      type="text"
                      value="—"
                      readOnly
                      style={readOnlyStyle}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Uploaded Work Order Copy (read-only link, no re-upload) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Copy of Work Order")}</label>
                </div>
                <div className="col-7">
                  {workOrderCopyFile ? (
                    <a
                      href={`${fileBaseUrl}/WorkOrderDocsImages/${workOrderCopyFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...readOnlyStyle,
                        display: "inline-block",
                        textDecoration: "none",
                      }}
                    >
                      📄 {workOrderCopyFile}
                    </a>
                  ) : (
                    <input
                      type="text"
                      value="—"
                      readOnly
                      style={readOnlyStyle}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Uploaded Measurement Book (read-only link, no re-upload) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Measurement Book")}</label>
                </div>
                <div className="col-7">
                  {measurementBookFile ? (
                    <a
                      href={`${fileBaseUrl}/Measurement_BookImages/${measurementBookFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...readOnlyStyle,
                        display: "inline-block",
                        textDecoration: "none",
                      }}
                    >
                      📄 {measurementBookFile}
                    </a>
                  ) : (
                    <input
                      type="text"
                      value="—"
                      readOnly
                      style={readOnlyStyle}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Uploaded Inspection Report (read-only link, no re-upload) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Inspection Report")}</label>
                </div>
                <div className="col-7">
                  {inspectionReportFile ? (
                    <a
                      href={`${fileBaseUrl}/Inspection_ReportImages/${inspectionReportFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...readOnlyStyle,
                        display: "inline-block",
                        textDecoration: "none",
                      }}
                    >
                      📄 {inspectionReportFile}
                    </a>
                  ) : (
                    <input
                      type="text"
                      value="—"
                      readOnly
                      style={readOnlyStyle}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Uploaded Work Completion Certificate (read-only link, no re-upload) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Work Completion")}</label>
                </div>
                <div className="col-7">
                  {workCompletionFile ? (
                    <a
                      href={`${fileBaseUrl}/Work_completionImages/${workCompletionFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...readOnlyStyle,
                        display: "inline-block",
                        textDecoration: "none",
                      }}
                    >
                      📄 {workCompletionFile}
                    </a>
                  ) : (
                    <input
                      type="text"
                      value="—"
                      readOnly
                      style={readOnlyStyle}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Uploaded Administrative Approved GR (read-only link, no re-upload) ── */}
            <div className="col-12 elements">
              <div className="row">
                <div className="col-3">
                  <label>{t("Administrative Approved GR")}</label>
                </div>
                <div className="col-7">
                  {grCopyFile ? (
                    <a
                      href={`${fileBaseUrl}/UploadedGR/${grCopyFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...readOnlyStyle,
                        display: "inline-block",
                        textDecoration: "none",
                      }}
                    >
                      📄 {grCopyFile}
                    </a>
                  ) : (
                    <input
                      type="text"
                      value="—"
                      readOnly
                      style={readOnlyStyle}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── Amount Summary (same 3-box layout as create page) ── */}
            <div className="col-12 elements">
              <div className="col-12 my-5">
                <div className="row">
                  <div className="col-4 part1 py-2">
                    <div className="row">
                      <div className="col-7 my-auto">
                        <p className="my-auto">{t("Total Bill Amount")} : </p>
                      </div>
                      <div className="col-5 my-auto">
                        <p className="my-auto">
                          <b>{Number(data.TotalBillAmount || 0)}</b>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-4 part1 py-2">
                    <div className="row">
                      <div className="col-8 my-auto">
                        <p className="my-auto">
                          {t("Total Deduction Amount")} :{" "}
                        </p>
                      </div>
                      <div className="col-4 my-auto">
                        <p className="my-auto">
                          <b>{totalDeductionAmount.toFixed(2)}</b>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-4 part1 py-2">
                    <div className="row">
                      <div className="col-7 my-auto">
                        <p className="my-auto">{t("Net Payable Amount")} : </p>
                      </div>
                      <div className="col-5 my-auto">
                        <p className="my-auto">
                          <b>{netPayableAmount.toFixed(2)}</b>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Deductions (EDITABLE amounts — same table as create page) ── */}
            <div className="tableContainer m-auto">
              <table>
                <thead>
                  <tr style={{ textAlign: "center" }}>
                    <th style={{ width: "5%" }}>{t("Sr.No")}</th>
                    <th style={{ width: "20%" }}>{t("Deduction Name")}</th>
                    <th style={{ width: "20%" }}>{t("Deduction Amount")}</th>
                    <th style={{ width: "20%" }}>{t("Bank Name")}</th>
                    <th style={{ width: "20%" }}>{t("Account Number")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fetchDeductionData.length > 0 ? (
                    fetchDeductionData.map((val, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{val.UseFor}</td>
                        <td>
                          <input
                            type="number"
                            value={val.DeductionAmount ?? "" }
                            placeholder="0"
                            min={0}
                            onChange={(e) =>
                              handleDeductionAmountChange(e, index)
                            }
                            onBlur={(e) => handleDeductionAmountBlur(e, index)}
                            onKeyDown={(e) => {
                              // Prevent minus, plus, decimal and exponent
                              if (["-", "+", "e", "E"].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </td>
                        <td>{val.Bank_Name}</td>
                        <td>{val.Account_Number}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        {t("No data found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Save / Cancel Buttons ── */}
            <div className="col-12 ms-auto my-5">
              <div className="row">
                <div className="col-7 m-auto">
                  <div className="col-12 text-center">
                    <button
                      type="submit"
                      className="px-4 py-2 btn btn-primary fw-bold"
                      disabled={saving || submitLockRef.current}
                      style={{
                        ...buttonStylePrimary,
                        borderRadius: "8px",
                        cursor: saving ? "not-allowed" : "pointer",
                        background: saving
                          ? "#9ca3af"
                          : "linear-gradient(to right, #3b409e, #0067ab, #0f79ba, #0098d5)",
                        opacity: saving ? 0.7 : 1,
                      }}
                    >
                      {saving ? (
                        <>
                          <span
                            style={{
                              width: "14px",
                              height: "14px",
                              border: "2px solid white",
                              borderTop: "2px solid transparent",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                          {t("Saving...")}
                        </>
                      ) : (
                        <>
                          <img
                            src="/Submit-32.png"
                            alt="submit"
                            style={{ width: "13px" }}
                          />
                          {t("Update & Resubmit")}
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="px-4 py-2 btn btn-danger fw-bold mx-4"
                      onClick={() =>
                        navigate("/AdminDashboard/ViewDemandGeneration")
                      }
                      style={{ ...buttonStyleDanger, borderRadius: "8px" }}
                    >
                      <img
                        src="/Close-32.png"
                        alt="cancel"
                        style={{ width: "13px", height: "13px" }}
                      />
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditDemandGeneration;
